// AI search (Ask mode) — client integration against Cogent²'s platform AI.
//
// Contract lives in docs/ai-search-spec.md. In short:
//   POST <endpoint>
//   Body: { question, sessionId, turnstileToken?, locale? }
//   Returns: SSE stream of `meta`, `delta`, `sources`, `followups`, `done`, `error`
//   Pre-flight errors: 429 (rate limited), 403 (turnstile), 400 (invalid),
//                      503 (kill switch)
//
// This module:
//   1. Reads config from the JSON blob emitted by ai-search-config.liquid
//   2. Resolves visibility (Off / Staff-with-cookie-or-URL / Everyone)
//   3. Injects the mode toggle pill + Ask panel scaffold into the search modal
//   4. Wires the toggle, persists choice in localStorage
//   5. In Ask mode: submits questions, streams the answer, renders sources,
//      renders follow-up pills, handles errors + rate-limit UX
//
// No dependency on Turnstile until Dave enables server-side enforcement.
// When the site key is present in settings, we lazy-load the Turnstile
// script and attach the widget; when absent, we send without a token
// (Dave has enforcement flagged during development).

// ─── Constants ─────────────────────────────────────────────────────────────

const SESSION_KEY = 'cogent-ask-session-id';
const MODE_KEY = 'cogent-ask-mode';
const STAFF_COOKIE = 'cogent-ask-staff';
const MAX_QUESTION_LEN = 4000;

// Source `type` → dot colour class. Dave's platform contract emits
// two type values today: `comparison` (compare-blog article) and
// `guide` (Intelligence guide / advisory content). Older values kept
// as a fallback in case the API ever emits richer taxonomy.
const SOURCE_TYPE_DOT = {
  comparison: '--violet',
  guide: '--sky',
  entity: '--violet',
  ecosystem: '--fuchsia',
  failure_pattern: '--emerald',
  vendor: '--amber',
  advisory: '--sky',
};

// Loading label copy for each `status.phase` — Dave sends "searching"
// early and "composing" once retrieval is done. Instant switch keeps
// the multi-second wait feeling alive.
const STATUS_LABELS = {
  default: 'Thinking…',
  searching: 'Searching the Intelligence base…',
  composing: 'Composing your answer…',
};

// ─── Config bootstrap ──────────────────────────────────────────────────────

function readConfig() {
  const node = document.querySelector('script[data-ai-search-config]');
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (err) {
    console.warn('[ai-search] invalid config JSON, disabling:', err);
    return null;
  }
}

function isStaffCookiePresent() {
  return document.cookie.split(';').some((c) => c.trim().startsWith(`${STAFF_COOKIE}=`));
}

function hasStaffUrlParam() {
  try {
    const params = new URLSearchParams(location.search);
    const v = params.get('ask');
    return v === 'on' || v === 'mock';
  } catch {
    return false;
  }
}

/**
 * If `?ask=mock` is in the URL, we short-circuit the real endpoint and
 * emit a synthetic SSE stream from JS. Two scenarios are supported so
 * the "grounded happy path" and the "out-of-scope nudge" states can
 * both be exercised:
 *   ?ask=mock                       → normal grounded answer + sources
 *   ?ask=mock&scenario=outofscope   → done with grounded:false
 * Returns the scenario name, or null when live mode.
 */
function mockScenarioFromUrl() {
  try {
    const params = new URLSearchParams(location.search);
    if (params.get('ask') !== 'mock') return null;
    return params.get('scenario') || 'default';
  } catch {
    return null;
  }
}

/**
 * Should the Ask toggle be visible to THIS visitor?
 * - Off: never
 * - Everyone: always
 * - Staff: only when `?ask=on` is in the URL OR the `cogent-ask-staff`
 *   cookie is set. Both are checked client-side because Shopify Liquid
 *   doesn't reliably expose arbitrary query params.
 *
 * When `?ask=on` is present, we also set the staff cookie so the
 * visitor stays in staff mode for the rest of the session even after
 * they navigate to a URL without the param.
 */
function isAskEnabled(config) {
  if (!config) return false;
  if (config.visibility === 'off') return false;
  if (config.visibility === 'everyone') return true;
  // staff
  const hasUrl = hasStaffUrlParam();
  const hasCookie = isStaffCookiePresent();
  if (hasUrl && !hasCookie) {
    // Persist for the session so subsequent navigations keep the toggle.
    try {
      document.cookie = `${STAFF_COOKIE}=1; path=/; SameSite=Lax`;
    } catch {
      /* ignore */
    }
  }
  return hasUrl || hasCookie;
}

// ─── sessionId ─────────────────────────────────────────────────────────────

function getSessionId() {
  let id = null;
  try {
    id = localStorage.getItem(SESSION_KEY);
  } catch {
    /* localStorage unavailable — private mode etc. Fall through to per-tab id. */
  }
  if (!id) {
    id = crypto.randomUUID
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      localStorage.setItem(SESSION_KEY, id);
    } catch {
      /* ignore */
    }
  }
  return id;
}

// ─── Mode persistence ──────────────────────────────────────────────────────

function readMode() {
  try {
    const m = localStorage.getItem(MODE_KEY);
    return m === 'ask' ? 'ask' : 'search';
  } catch {
    return 'search';
  }
}

function writeMode(mode) {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

// ─── Turnstile (lazy) ──────────────────────────────────────────────────────

let turnstileScriptPromise = null;

function loadTurnstile() {
  if (turnstileScriptPromise) return turnstileScriptPromise;
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onCogentTurnstileReady';
    s.async = true;
    s.defer = true;
    s.onerror = reject;
    window.onCogentTurnstileReady = () => resolve(window.turnstile);
    document.head.appendChild(s);
  });
  return turnstileScriptPromise;
}

async function mountTurnstile(container, siteKey) {
  if (!siteKey || !container) return null;
  try {
    const ts = await loadTurnstile();
    return ts.render(container, {
      sitekey: siteKey,
      size: 'invisible',
      appearance: 'interaction-only',
    });
  } catch (err) {
    console.warn('[ai-search] turnstile load failed:', err);
    return null;
  }
}

async function getTurnstileToken(widgetId) {
  if (!widgetId || !window.turnstile) return null;
  try {
    return await new Promise((resolve, reject) => {
      window.turnstile.execute(widgetId, {
        callback: resolve,
        'error-callback': reject,
      });
      // Fallback: execute returns synchronously in some flows
      const existing = window.turnstile.getResponse(widgetId);
      if (existing) resolve(existing);
    });
  } catch (err) {
    console.warn('[ai-search] turnstile execute failed:', err);
    return null;
  }
}

// ─── SSE parser ────────────────────────────────────────────────────────────

/**
 * Parse a fetch Response's SSE stream and dispatch each event to a handler.
 * Handles named events (event: X\ndata: Y), heartbeat comments (: hb), and
 * multi-line data payloads. Resolves when the stream closes.
 */
async function consumeSSE(response, handlers) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = 'message';
  let currentData = [];

  const dispatch = () => {
    if (!currentData.length) {
      currentEvent = 'message';
      return;
    }
    const dataStr = currentData.join('\n');
    let payload = dataStr;
    try {
      payload = JSON.parse(dataStr);
    } catch {
      /* leave as raw string — Dave's contract is all JSON but be forgiving */
    }
    const handler = handlers[currentEvent];
    if (handler) handler(payload);
    currentEvent = 'message';
    currentData = [];
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      dispatch();
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    // Split by \n but keep the final partial line in buffer for next chunk
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const rawLine of lines) {
      const line = rawLine.replace(/\r$/, '');
      if (line === '') {
        // Blank line ends the current event block
        dispatch();
      } else if (line.startsWith(':')) {
        // SSE comment (heartbeat) — ignore
      } else if (line.startsWith('event:')) {
        currentEvent = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        currentData.push(line.slice(5).replace(/^ /, ''));
      } else if (line.startsWith('retry:')) {
        // We ignore server-suggested retry intervals; not relevant
        // because we don't auto-reconnect on failure.
      }
    }
  }
}

// ─── Ask request ───────────────────────────────────────────────────────────

/**
 * Fire the Ask request and return the fetch Response. Throws for network
 * failures; the caller handles response.ok checks + pre-flight statuses.
 */
async function sendQuestion({ endpoint, question, sessionId, turnstileToken }) {
  const body = { question, sessionId };
  if (turnstileToken) body.turnstileToken = turnstileToken;

  return fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'omit',
  });
}

// ─── Mock stream (for local iteration without a live endpoint) ─────────────

/**
 * Build a fake Response whose body is a ReadableStream emitting a plausible
 * SSE conversation. Same event contract as the real endpoint, so
 * `consumeSSE` doesn't need any mock-awareness.
 *
 * Two scenarios:
 *   'default' — grounded happy path with sources + follow-ups
 *   'outofscope' — AI says "I don't have coverage", triggers the nudge
 */
function buildMockResponse(scenario) {
  const encoder = new TextEncoder();
  const sse = (event, data) =>
    encoder.encode(`event: ${event}\ndata: ${JSON.stringify(data)}\n\n`);
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const stream = new ReadableStream({
    async start(controller) {
      // meta ─────────────────────────────
      controller.enqueue(sse('meta', { sessionId: 'mock-session', grounded: null }));
      await wait(200);

      // Scenario-specific payload
      let text;
      let sources = [];
      let followups = [];
      let grounded = true;
      let stopReason = 'complete';

      if (scenario === 'outofscope') {
        text =
          "That specific angle isn't something our Intelligence base covers strongly right now — it's the kind of nuanced call that a live Cogent² conversation handles better than an AI grounded on a static knowledge set.";
        followups = [
          'What can you help me with?',
          'Show me your latest comparison articles',
          'How does Cogent² approach integration projects?',
        ];
        grounded = false;
        stopReason = 'out_of_scope';
      } else {
        text =
          "For a £25m fashion retailer, NetSuite is the safer bet than Cin7 Core — the multi-entity accounting depth handles complexity that Cin7 Core buckles under at that scale, and NetSuite's warehouse model matches how larger fashion operators actually work.\n\nThat said, NetSuite's implementation cost is 3–5× higher and the timeline runs 6–9 months rather than 6–9 weeks. Cin7 Core still wins for retailers under £10m who need something operational this quarter — but by £25m the ceiling has arrived, and migrating later costs more than picking NetSuite now.\n\nWherever you land, Patchworks is the connective tissue. The ERP is only ever as reliable as the data flowing into it, and manual reconciliation between systems is where most £25m fashion brands lose margin.";
        sources = [
          {
            id: 'src-netsuite',
            title: 'NetSuite',
            url: '/pages/intelligence-netsuite',
            type: 'entity',
            snippet: 'ERP entity profile',
          },
          {
            id: 'src-cin7',
            title: 'Cin7 Core',
            url: '/pages/intelligence-cin7-core',
            type: 'entity',
            snippet: 'ERP entity profile',
          },
          {
            id: 'src-fashion',
            title: 'Fashion & apparel ecosystem',
            url: '/pages/intelligence-ecosystem-fashion-retail',
            type: 'ecosystem',
            snippet: null,
          },
          {
            id: 'src-patchworks',
            title: 'Patchworks',
            url: '/pages/intelligence-patchworks',
            type: 'vendor',
            snippet: null,
          },
        ];
        followups = [
          'What if I chose Cin7 Core instead?',
          'When should I migrate to a bigger ERP?',
          'How does Patchworks fit into this?',
        ];
      }

      // Stream the answer word-by-word (typewriter effect)
      const chunks = text.split(/(\s+)/); // preserves whitespace
      for (const chunk of chunks) {
        if (!chunk) continue;
        controller.enqueue(sse('delta', { text: chunk }));
        // 20–60ms per chunk feels natural without being sluggish
        await wait(20 + Math.random() * 40);
      }

      await wait(250);
      controller.enqueue(sse('sources', { sources }));
      await wait(200);
      if (followups.length > 0) {
        controller.enqueue(sse('followups', { suggestions: followups }));
      }
      await wait(150);
      controller.enqueue(sse('done', { sessionId: 'mock-session', stopReason, grounded }));
      controller.close();
    },
  });

  return new Response(stream, {
    status: 200,
    headers: { 'Content-Type': 'text/event-stream' },
  });
}

// ─── UI helpers ────────────────────────────────────────────────────────────

function el(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v;
    else if (k === 'html') node.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v !== null && v !== undefined) node.setAttribute(k, v);
  }
  for (const c of children) {
    if (c == null) continue;
    node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

function escapeHTML(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// ─── Renderers ─────────────────────────────────────────────────────────────

function renderStarterPills(container, pills, onPick) {
  const list = el('ul', { class: 'ask-panel__starter-pills' });
  for (const p of pills) {
    list.appendChild(
      el(
        'li',
        {},
        el(
          'button',
          {
            type: 'button',
            class: 'ask-panel__starter-pill',
            // stopPropagation because submit() replaces the pill's
            // container mid-handler — without it the click bubbles to
            // the dialog's outside-click detector with an orphaned
            // target and the modal closes.
            onclick: (e) => {
              e.stopPropagation();
              onPick(p);
            },
          },
          p,
        ),
      ),
    );
  }
  container.replaceChildren(
    el('p', { class: 'ask-panel__starter-lead' }, 'Try asking…'),
    list,
  );
}

function renderSources(container, sources) {
  if (!Array.isArray(sources) || sources.length === 0) {
    container.hidden = true;
    return;
  }
  container.hidden = false;
  container.replaceChildren(
    el('p', { class: 'ask-panel__sources-lead' }, 'Sources'),
    el(
      'ul',
      { class: 'ask-panel__sources-list' },
      ...sources.map((s) => {
        const dotClass = SOURCE_TYPE_DOT[s.type] || '--violet';
        return el(
          'li',
          {},
          el(
            'a',
            {
              class: 'ask-panel__source-chip',
              href: s.url,
              title: s.snippet || '',
            },
            el('span', {
              class: `ask-panel__source-dot ask-panel__source-dot${dotClass}`,
              'aria-hidden': 'true',
            }),
            document.createTextNode(s.title || s.url),
          ),
        );
      }),
    ),
  );
}

function renderFollowups(container, suggestions, onPick) {
  if (!Array.isArray(suggestions) || suggestions.length === 0) {
    container.hidden = true;
    return;
  }
  container.hidden = false;
  container.replaceChildren(
    el('p', { class: 'ask-panel__followups-lead' }, 'Also ask about…'),
    el(
      'ul',
      { class: 'ask-panel__followups-list' },
      ...suggestions.map((s) =>
        el(
          'li',
          {},
          el(
            'button',
            {
              type: 'button',
              class: 'ask-panel__followup-pill',
              // See starter-pill comment: stopPropagation prevents the
              // dialog's outside-click detector from misreading the
              // orphan target that submit() creates when it clears
              // this container.
              onclick: (e) => {
                e.stopPropagation();
                onPick(s);
              },
            },
            s,
          ),
        ),
      ),
    ),
  );
}

function renderNudge(container, contactUrl) {
  container.hidden = false;
  container.replaceChildren(
    el(
      'p',
      { class: 'ask-panel__nudge-text' },
      "That's at the edge of our Intelligence coverage — want a Cogent² consultant to weigh in? ",
      el(
        'a',
        { href: contactUrl, class: 'ask-panel__nudge-link' },
        'Talk to us →',
      ),
    ),
  );
}

// ─── Markdown ──────────────────────────────────────────────────────────────

/**
 * Tiny, safe markdown → HTML renderer. Dave's platform returns answers
 * with markdown formatting — `**bold**`, `*italic*`, bullet lists,
 * `[text](url)` links, inline `code`, and double-newline paragraphs.
 * Without rendering, users see the raw asterisks in the answer.
 *
 * We deliberately don't pull in a full markdown lib (marked / markdown-it
 * are ~40kb minified) — the surface area of what Dave emits is small
 * and easy to handle inline. Anything unsupported falls through as text.
 *
 * Safety: input is HTML-escaped first, so raw HTML in a model response
 * can't inject markup. Links are restricted to http(s) / mailto / tel /
 * root-relative to block `javascript:` and other exotic schemes.
 */
function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeHref(url) {
  const u = String(url || '').trim();
  if (/^(https?:|mailto:|tel:)/i.test(u)) return u;
  if (u.startsWith('/') || u.startsWith('#')) return u;
  return '#';
}

/**
 * Strip markdown *for display in the typewriter*. During the reveal
 * animation we're inserting raw text into `textContent`, so any `**`,
 * backticks, or `[label](url)` syntax shows up literally on screen —
 * users see "**Connect Shopify**" for a second before the final swap
 * to bold. This helper removes the syntax so the typewriter reveals
 * clean prose that lines up with the rendered markdown that lands
 * once the answer completes.
 *
 * Only fully closed pairs are stripped — an unclosed `**` at the end
 * of the current accumulated text stays intact until the closing pair
 * arrives in a later delta. The controller re-strips the FULL raw
 * buffer on every delta, so once the closer lands both markers vanish
 * together on the next tick.
 */
function stripMarkdownForDisplay(text) {
  if (!text) return '';
  let out = text;
  // Links → just label. Do this first so brackets/parens aren't
  // treated as anything else.
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '$1');
  // Inline code — drop the backticks, keep the content.
  out = out.replace(/`([^`\n]+)`/g, '$1');
  // Bold — must be closed on the same line.
  out = out.replace(/\*\*([^*\n]+)\*\*/g, '$1');
  // Italic — single asterisks, closed on same line, not part of **.
  out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1$2');
  // Leading bullet marker at line-start — replace with a real bullet
  // char so the typewriter still shows list structure while the
  // proper <ul> hasn't rendered yet.
  out = out.replace(/^\s*[-*]\s+/gm, '• ');
  return out;
}

function renderMarkdown(src) {
  if (!src) return '';
  // 1. Escape HTML entities up front so any raw `<foo>` in the model
  //    output is treated as literal text.
  let text = escapeHtml(src);

  // 2. Inline code: `foo` → <code>foo</code>. Do this before bold/italic
  //    so `**` inside backticks isn't consumed.
  text = text.replace(/`([^`\n]+)`/g, (_m, code) => `<code>${code}</code>`);

  // 3. Links: [text](url) → <a href="url">text</a>. Href is sanitised.
  text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label, href) => {
    return `<a href="${safeHref(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });

  // 4. Bold: **foo** → <strong>foo</strong>. Non-greedy, doesn't cross
  //    newlines, requires non-space at boundaries so `** ` won't match.
  text = text.replace(/\*\*(\S.*?\S|\S)\*\*/g, '<strong>$1</strong>');

  // 5. Italic: single-asterisk *foo* — must not eat the ** already
  //    replaced above. Since we ran bold first, remaining `*` pairs are
  //    italic. Same non-space boundary trick.
  text = text.replace(/(^|[^*])\*(\S.*?\S|\S)\*(?!\*)/g, '$1<em>$2</em>');

  // 6. Block structure. Split on blank lines → paragraphs. Within each
  //    paragraph, if every line starts with `- ` or `* `, treat as UL.
  const blocks = text.split(/\n{2,}/);
  const html = blocks
    .map((block) => {
      const lines = block.split('\n');
      const isBulletList = lines.every((ln) => /^\s*[-*]\s+/.test(ln));
      if (isBulletList) {
        const items = lines
          .map((ln) => ln.replace(/^\s*[-*]\s+/, '').trim())
          .map((li) => `<li>${li}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }
      // Normal paragraph — join with <br> to preserve single-newline breaks.
      return `<p>${lines.join('<br>')}</p>`;
    })
    .join('');

  return html;
}

// ─── Typewriter ────────────────────────────────────────────────────────────

/**
 * Client-side typewriter animation. Dave's platform emits all `delta`
 * events in a burst at the end of generation (server safety-checks the
 * whole answer before streaming), so we can't rely on delta arrival
 * timing to feel live. Instead we buffer everything that arrives and
 * reveal it on a rAF loop at a steady human-readable pace.
 *
 * Handles both worlds gracefully — if the platform ever moves to true
 * token streaming, this still just consumes text as it arrives.
 */
class Typewriter {
  constructor(el) {
    this.el = el;
    this.buffer = '';
    this.revealedLen = 0;
    this.rafHandle = null;
    // ~200 chars/sec at 60fps. Feels quick but readable — the point is
    // to feel deliberate, not to force the user to wait.
    this.charsPerFrame = 4;
    // Resolvers waiting for the buffer to fully reveal.
    this.completionResolvers = [];
  }

  append(text) {
    if (!text) return;
    this.buffer += text;
    this.play();
  }

  /**
   * Replace the reveal buffer with a new (usually-longer) string. Used
   * when the display text is re-derived from an ever-growing raw source
   * (see stripMarkdownForDisplay). If the new buffer is shorter than
   * what we've already revealed — which can happen when an unclosed
   * `**` gets closed and both markers vanish — we clamp revealedLen
   * to the new length; the visible text either stays the same or
   * shortens by a couple of chars, which is far less jarring than
   * showing literal `**` mid-reveal.
   */
  setBuffer(text) {
    this.buffer = text || '';
    if (this.revealedLen > this.buffer.length) {
      this.revealedLen = this.buffer.length;
      this.el.textContent = this.buffer.slice(0, this.revealedLen);
    }
    this.play();
  }

  play() {
    if (this.rafHandle != null) return; // already animating
    const step = () => {
      if (this.revealedLen >= this.buffer.length) {
        this.rafHandle = null;
        this.completionResolvers.splice(0).forEach((r) => r());
        return;
      }
      this.revealedLen = Math.min(this.buffer.length, this.revealedLen + this.charsPerFrame);
      this.el.textContent = this.buffer.slice(0, this.revealedLen);
      this.rafHandle = requestAnimationFrame(step);
    };
    this.rafHandle = requestAnimationFrame(step);
  }

  /** Resolves when the current buffer has been fully revealed. */
  whenComplete() {
    if (this.revealedLen >= this.buffer.length && this.rafHandle == null) {
      return Promise.resolve();
    }
    return new Promise((r) => this.completionResolvers.push(r));
  }

  reset() {
    this.buffer = '';
    this.revealedLen = 0;
    this.el.textContent = '';
    if (this.rafHandle != null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.completionResolvers.splice(0).forEach((r) => r());
  }
}

// ─── Main controller ───────────────────────────────────────────────────────

class AskModeController {
  constructor({ modal, config }) {
    this.modal = modal;
    this.config = config;
    this.sessionId = getSessionId();
    this.currentMode = readMode();
    this.turnstileWidgetId = null;
    this.abortController = null;

    this.inputEl = modal.querySelector('.search-input');
    this.form = modal.querySelector('.predictive-search-form');
    this.resultsWrapper = modal.querySelector('.predictive-search-form__content-wrapper');

    if (!this.inputEl || !this.form || !this.resultsWrapper) {
      console.warn('[ai-search] required search-modal elements missing — aborting');
      return;
    }

    this.mountToggle();
    this.mountAskPanel();
    // Typewriter revealer for the answer text — see class comment.
    // Instantiated once and reused across every question via reset().
    this.typewriter = new Typewriter(this.answerTextEl);
    // Raw markdown accumulator — deltas append here in their original
    // form. The typewriter is fed a syntax-stripped version for the
    // live reveal, and this raw buffer is what we render as HTML once
    // the stream completes. Kept on the controller (not the typewriter)
    // so the typewriter stays a pure display component.
    this.rawAnswer = '';
    this.applyMode(this.currentMode);

    // Mount Turnstile lazily — only when the site key is present. Enforcement
    // is flagged on Dave's side, so we can build without it.
    if (this.config.turnstileSiteKey) {
      mountTurnstile(this.turnstileContainer, this.config.turnstileSiteKey).then((id) => {
        this.turnstileWidgetId = id;
      });
    }

    // Intercept Enter in the input when in Ask mode.
    //
    // Horizon binds its own `onSearchKeyDown` handler (from
    // predictive-search.js) directly to this same input element via
    // the `on:keydown="/onSearchKeyDown"` binding. Its Enter branch
    // does `window.location.href = /search?q=...` — a direct
    // navigation, NOT a form submit — so a submit-event listener on
    // the form can never catch it, and stopPropagation() alone won't
    // stop the sibling handler because it's registered on the same
    // element.
    //
    // Fix: register in capture phase (so we run before Horizon) and
    // use stopImmediatePropagation() (so Horizon's listener on the
    // same element is skipped entirely).
    this.inputEl.addEventListener(
      'keydown',
      (e) => {
        if (this.currentMode !== 'ask') return;
        if (e.key !== 'Enter') return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const q = (this.inputEl.value || '').trim();
        if (q) this.submit(q);
      },
      true,
    );

    // Belt-and-braces: also intercept form submission itself. The Enter
    // handler above catches the common path, but the form still contains
    // the visible "View all results" button (which is a submit button)
    // plus mobile keyboards / accessibility paths that may fire a submit
    // event without a keydown. If any of those fire in Ask mode, we'd
    // navigate to /search?q=... — the search-results page has no AI, so
    // the user would lose their in-flight answer for no gain. Trap it
    // here and route through the same Ask submit() as Enter.
    // The listener is added in the capture phase so we win against
    // Horizon's own bubbling handler on the form.
    this.form.addEventListener(
      'submit',
      (e) => {
        if (this.currentMode !== 'ask') return;
        e.preventDefault();
        e.stopPropagation();
        const q = (this.inputEl.value || '').trim();
        if (q) this.submit(q);
      },
      true,
    );
  }

  mountToggle() {
    this.toggleEl = el(
      'div',
      { class: 'search-modal__mode-toggle', role: 'tablist', 'aria-label': 'Search mode' },
      el(
        'button',
        {
          type: 'button',
          class: 'search-modal__mode-btn',
          'data-mode': 'search',
          role: 'tab',
          onclick: () => this.setMode('search'),
        },
        'Search',
      ),
      el(
        'button',
        {
          type: 'button',
          class: 'search-modal__mode-btn',
          'data-mode': 'ask',
          role: 'tab',
          onclick: () => this.setMode('ask'),
        },
        el('span', { class: 'search-modal__mode-sparkle', 'aria-hidden': 'true' }, '✨'),
        document.createTextNode(' Ask'),
      ),
    );
    // Insert above the results wrapper.
    this.resultsWrapper.parentNode.insertBefore(this.toggleEl, this.resultsWrapper);
  }

  mountAskPanel() {
    // Turnstile invisible-widget container (attached inside the modal so it
    // has a valid DOM position; no visual footprint).
    this.turnstileContainer = el('div', {
      class: 'ask-panel__turnstile',
      'aria-hidden': 'true',
    });

    this.starterEl = el('div', { class: 'ask-panel__starter', 'data-state': 'starter' });
    // The loading label is dynamic — updated by `status` events during
    // the wait (searching / composing). Default is "Thinking…" until
    // the first status arrives.
    this.loadingLabelEl = el('span', { class: 'ask-panel__loading-label' }, STATUS_LABELS.default);
    this.loadingEl = el(
      'div',
      { class: 'ask-panel__loading', 'data-state': 'loading', hidden: '' },
      this.loadingLabelEl,
    );
    this.answerTextEl = el('div', { class: 'ask-panel__answer-text' });
    this.sourcesEl = el('div', { class: 'ask-panel__sources', hidden: '' });
    this.followupsEl = el('div', { class: 'ask-panel__followups', hidden: '' });
    this.nudgeEl = el('div', { class: 'ask-panel__nudge', hidden: '' });
    this.errorEl = el('div', { class: 'ask-panel__error', hidden: '' });
    this.resetBtn = el(
      'button',
      {
        type: 'button',
        class: 'ask-panel__reset',
        hidden: '',
        onclick: () => this.reset(),
      },
      'Ask another',
    );

    // Two-column top: answer text on the left, sources sidebar on
    // the right (Google-style). Below the row, follow-ups + nudge +
    // reset span the full width.
    this.answerTopEl = el(
      'div',
      { class: 'ask-panel__answer-top' },
      this.answerTextEl,
      this.sourcesEl,
    );

    this.answerEl = el(
      'div',
      { class: 'ask-panel__answer', 'data-state': 'answering', hidden: '' },
      this.answerTopEl,
      this.followupsEl,
      this.nudgeEl,
      this.resetBtn,
    );

    this.panelEl = el(
      'div',
      { class: 'search-modal__ask-panel', hidden: '' },
      this.turnstileContainer,
      this.starterEl,
      this.loadingEl,
      this.answerEl,
      this.errorEl,
    );

    // Populate starter with pills.
    renderStarterPills(this.starterEl, this.config.starterPills || [], (question) => {
      this.inputEl.value = question;
      this.submit(question);
    });

    // Insert the panel INSIDE the results wrapper so it lives in the same
    // scroll context. Show/hide is controlled via `[hidden]` and CSS.
    this.resultsWrapper.appendChild(this.panelEl);
  }

  applyMode(mode) {
    this.currentMode = mode;
    this.modal.dataset.askMode = mode;
    // Toggle aria-selected on the buttons.
    for (const btn of this.toggleEl.querySelectorAll('.search-modal__mode-btn')) {
      const active = btn.dataset.mode === mode;
      btn.setAttribute('aria-selected', String(active));
      btn.classList.toggle('search-modal__mode-btn--active', active);
    }
    // Show/hide the ask panel.
    this.panelEl.hidden = mode !== 'ask';

    // Neutralise Horizon's on:keydown / on:input / on:submit bindings while
    // in Ask mode.
    //
    // Horizon's component.js registers ONE document-level delegate per event
    // type in the capture phase, then routes each event to the closest
    // element carrying an `on:<event>` attribute. That means:
    //   - It fires BEFORE any listener we attach on the input itself
    //     (capture phase descends from document downward).
    //   - Its Enter branch in onSearchKeyDown does
    //     `window.location.href = /search?q=...`, so pressing Enter in
    //     Ask mode was navigating the browser away from our streamed
    //     answer.
    //   - There's no `event.currentTarget` we can win on and no way to
    //     stopImmediatePropagation() from a downstream listener.
    //
    // The clean fix is to make the attribute selector not match at all
    // while in Ask mode. We swap `on:keydown` → `data-on-keydown-ask`
    // (and same for submit / input on relevant nodes), and restore them
    // when switching back to Search mode. Cheap, reversible, and it
    // means EVERY Horizon binding on those nodes is neutralised in one
    // pass — including the `/onSearchKeyDown` on the form, the `/search`
    // input handler, and any future ones the platform adds.
    const askOffElements = [this.inputEl, this.form];
    const askOffEvents = ['keydown', 'submit', 'input'];
    for (const node of askOffElements) {
      if (!node) continue;
      for (const evt of askOffEvents) {
        const live = `on:${evt}`;
        const parked = `data-ask-parked-on-${evt}`;
        if (mode === 'ask') {
          if (node.hasAttribute(live)) {
            node.setAttribute(parked, node.getAttribute(live) || '');
            node.removeAttribute(live);
          }
        } else if (node.hasAttribute(parked)) {
          node.setAttribute(live, node.getAttribute(parked) || '');
          node.removeAttribute(parked);
        }
      }
    }

    // Update input placeholder.
    if (mode === 'ask') {
      this.inputEl.dataset.searchPlaceholder = this.inputEl.dataset.searchPlaceholder || this.inputEl.placeholder;
      this.inputEl.placeholder = 'Ask about retail tech, ecosystems, failure patterns…';
    } else if (this.inputEl.dataset.searchPlaceholder) {
      this.inputEl.placeholder = this.inputEl.dataset.searchPlaceholder;
    }
  }

  setMode(mode) {
    if (mode === this.currentMode) return;
    writeMode(mode);
    this.applyMode(mode);
    if (mode === 'search') {
      // Clear the query when returning to Search so a leftover Ask
      // question doesn't leak into the predictive-search endpoint.
      // Dispatch an `input` event so the predictive-search JS resets
      // its results panel to the empty state (recently viewed etc).
      if (this.inputEl.value) {
        this.inputEl.value = '';
        this.inputEl.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.inputEl.focus();
    } else if (mode === 'ask') {
      this.inputEl.focus();
    }
  }

  reset() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.starterEl.hidden = false;
    this.loadingEl.hidden = true;
    this.loadingLabelEl.textContent = STATUS_LABELS.default;
    this.answerEl.hidden = true;
    this.errorEl.hidden = true;
    this.typewriter.reset();
    this.rawAnswer = '';
    this.sourcesEl.hidden = true;
    this.followupsEl.hidden = true;
    this.nudgeEl.hidden = true;
    this.inputEl.value = '';
    this.inputEl.focus();
  }

  showError(message) {
    this.loadingEl.hidden = true;
    this.starterEl.hidden = true;
    this.answerEl.hidden = true;
    this.errorEl.hidden = false;
    this.errorEl.replaceChildren(
      el('p', { class: 'ask-panel__error-text' }, message),
      el(
        'button',
        {
          type: 'button',
          class: 'ask-panel__error-retry',
          onclick: () => this.reset(),
        },
        'Try again',
      ),
    );
  }

  async submit(rawQuestion) {
    const question = (rawQuestion || '').trim();
    if (!question) return;
    if (question.length > MAX_QUESTION_LEN) {
      this.showError(`Question is a bit long — please keep it under ${MAX_QUESTION_LEN} characters.`);
      return;
    }

    // Cancel any in-flight previous request. Without this, a follow-up
    // question submitted while the previous stream is still open would
    // leave the old fetch running — it'd continue firing delta/sources/
    // done events into now-cleared containers, and (more importantly)
    // count as a second concurrent request against Dave's rate limiter,
    // which is what was tripping the 429 on follow-up questions.
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Clear any content from a previous question so the new stream
    // starts from an empty slate. Without this, delta events would
    // append onto the previous answer and stale sources / follow-ups
    // / nudge blocks would persist.
    this.typewriter.reset();
    this.rawAnswer = '';
    this.sourcesEl.replaceChildren();
    this.sourcesEl.hidden = true;
    this.followupsEl.replaceChildren();
    this.followupsEl.hidden = true;
    this.nudgeEl.replaceChildren();
    this.nudgeEl.hidden = true;
    this.resetBtn.hidden = true;
    this.loadingLabelEl.textContent = STATUS_LABELS.default;

    // State: loading (hide starter, show spinner)
    this.starterEl.hidden = true;
    this.answerEl.hidden = true;
    this.errorEl.hidden = true;
    this.loadingEl.hidden = false;

    // Grab a Turnstile token if the widget is mounted.
    let turnstileToken = null;
    if (this.turnstileWidgetId) {
      turnstileToken = await getTurnstileToken(this.turnstileWidgetId);
    }

    // Fire the request (or the mock, if ?ask=mock is in the URL).
    this.abortController = new AbortController();
    let response;
    const mockScenario = mockScenarioFromUrl();
    if (mockScenario) {
      response = buildMockResponse(mockScenario);
    } else {
      try {
        response = await sendQuestion({
          endpoint: this.config.endpoint,
          question,
          sessionId: this.sessionId,
          turnstileToken,
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('[ai-search] network error:', err);
        this.showError('Network hiccup — check your connection and try again.');
        return;
      }
    }

    // Pre-flight (non-SSE) failures.
    if (!response.ok) {
      let body = {};
      try {
        body = await response.json();
      } catch {
        /* ignore */
      }
      if (response.status === 429) {
        // Only quote a specific wait if Dave actually included Retry-After.
        // Without it we don't know if it's the burst limit (5/min) or the
        // hourly bucket (20/hr per IP), so hardcoding "30s" was actively
        // misleading — users would try again in 30s and get another 429.
        // Fall back to a duration-free message that says "wait a bit"
        // instead of naming a number we made up client-side.
        const retryHeader = parseInt(response.headers.get('Retry-After'), 10);
        if (retryHeader > 0) {
          const label = retryHeader >= 60 ? `${Math.round(retryHeader / 60)}m` : `${retryHeader}s`;
          this.showError(`Rate limit reached — try again in ${label}.`);
        } else {
          this.showError(
            "Rate limit reached — you've used your Ask quota for the moment. Try again in a few minutes.",
          );
        }
        return;
      }
      if (response.status === 403) {
        this.showError('Bot check failed — please refresh and try again.');
        return;
      }
      if (response.status === 400) {
        this.showError(body.message || 'That question looks unusable — try rephrasing.');
        return;
      }
      if (response.status === 503) {
        this.showError('Ask mode is temporarily unavailable. Please try Search instead.');
        // Also flip mode back to search so subsequent submits don't loop.
        this.setMode('search');
        return;
      }
      this.showError(`Unexpected error (${response.status}). Please try again.`);
      return;
    }

    // Response headers back — keep the spinner + status label visible.
    // Dave's platform buffers the whole answer server-side then flushes
    // all deltas in a burst at the end of generation. We keep the
    // spinner running through that wait, updated live via `status`
    // events, and only swap to the answer view once the first delta
    // arrives. The typewriter (below) then reveals the text at a
    // human-readable pace regardless of whether deltas trickle in or
    // arrive all at once.
    this.resetBtn.hidden = true;
    let firstDeltaSeen = false;

    // Track state during stream so we can react on `done`.
    let stopReason = null;
    let grounded = null;

    await consumeSSE(response, {
      meta: () => {
        /* echoed session id — nothing to do */
      },
      status: (payload) => {
        // Progress signal: "searching" → "composing" → first delta.
        const phase = payload && payload.phase;
        if (phase && STATUS_LABELS[phase]) {
          this.loadingLabelEl.textContent = STATUS_LABELS[phase];
        }
      },
      delta: (payload) => {
        if (!payload || typeof payload.text !== 'string') return;
        // First delta — swap the spinner for the answer container.
        if (!firstDeltaSeen) {
          firstDeltaSeen = true;
          this.loadingEl.hidden = true;
          this.answerEl.hidden = false;
        }
        // Accumulate the raw markdown (needed for the final HTML render)
        // and re-derive the syntax-stripped display text from the WHOLE
        // buffer, not this delta alone. Re-stripping the full buffer
        // handles the case where a `**` opener and its closer arrive
        // in separate deltas: per-delta stripping would leave the
        // opener visible; whole-buffer stripping cleans both up as
        // soon as the closer lands.
        this.rawAnswer += payload.text;
        this.typewriter.setBuffer(stripMarkdownForDisplay(this.rawAnswer));
      },
      sources: (payload) => {
        renderSources(this.sourcesEl, payload && payload.sources);
      },
      followups: (payload) => {
        renderFollowups(this.followupsEl, payload && payload.suggestions, (q) => {
          this.inputEl.value = q;
          this.submit(q);
        });
      },
      done: (payload) => {
        stopReason = payload && payload.stopReason;
        grounded = payload && payload.grounded;
      },
      error: (payload) => {
        console.warn('[ai-search] stream error:', payload);
        this.showError((payload && payload.message) || 'Something went wrong mid-answer. Please try again.');
      },
    }).catch((err) => {
      if (err.name === 'AbortError') return;
      console.warn('[ai-search] stream consume failed:', err);
    });

    // Wait for the typewriter to finish revealing before deciding on the
    // nudge / reveal state — feels more natural than showing the nudge
    // above still-unrevealed text.
    await this.typewriter.whenComplete();

    // Now that the plain-text typewriter has finished, swap in the
    // rendered markdown. The typewriter revealed a syntax-stripped
    // display buffer (see stripMarkdownForDisplay), so users never
    // saw literal `**` or backticks mid-reveal. The final render
    // uses the RAW accumulated answer to produce proper <strong>,
    // <ul>, links, etc.
    const rendered = renderMarkdown(this.rawAnswer);
    if (rendered) {
      this.answerTextEl.innerHTML = rendered;
    }

    // Nudge only on true out-of-scope. A `refused` answer (off-topic /
    // junk detection) uses the delta text itself as the polite
    // deflection — adding a "talk to a consultant" nudge on top would
    // read as spammy.
    if (stopReason === 'out_of_scope' || (grounded === false && stopReason !== 'refused')) {
      renderNudge(this.nudgeEl, this.config.contactUrl);
    }

    // Safety net: if the stream closed without any delta events firing
    // (edge case — done with no content, or an early error we didn't
    // catch), swap views so the user isn't left staring at a spinner.
    if (!firstDeltaSeen) {
      this.loadingEl.hidden = true;
      this.answerEl.hidden = false;
    }
    this.resetBtn.hidden = false;
  }
}

// ─── Boot ──────────────────────────────────────────────────────────────────

function boot() {
  const config = readConfig();
  if (!isAskEnabled(config)) return; // Off, or Staff without cookie/URL

  const modal = document.querySelector('.search-modal');
  if (!modal) return;

  // If the modal contains a <predictive-search-component>, we treat that as
  // the ready signal. Otherwise wait for it via a MutationObserver — the
  // modal renders on user click, so it's not always present at DOMContentLoaded.
  if (modal.querySelector('predictive-search-component')) {
    new AskModeController({ modal, config });
    return;
  }

  const observer = new MutationObserver(() => {
    if (modal.querySelector('predictive-search-component')) {
      observer.disconnect();
      new AskModeController({ modal, config });
    }
  });
  observer.observe(modal, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
